import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HiOutlineArrowUp } from 'react-icons/hi';

const MAX_LENGTH = 4000;
const MAX_ROWS_HEIGHT = 200;

/**
 * The composer from the reference: one rounded, elevated panel that grows with
 * the text. Enter sends, Shift+Enter makes a newline.
 */
export default function ChatComposer({ onSubmit, disabled, placeholder }) {
  const [value, setValue] = useState('');
  const textareaRef = useRef(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_ROWS_HEIGHT)}px`;
  }, []);

  useEffect(() => {
    resize();
  }, [value, resize]);

  const submit = useCallback(() => {
    const question = value.trim();
    if (!question || disabled) {
      return;
    }
    setValue('');
    onSubmit(question);
  }, [value, disabled, onSubmit]);

  const onKeyDown = event => {
    // Enter sends; Shift+Enter is a newline. IME composition must not be
    // interrupted, or committing a candidate would send the message.
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
      className="px-4 pb-4"
    >
      <div className="mx-auto w-full max-w-reading">
        <div
          className="flex items-end gap-2 rounded-2xl border border-hairline bg-surface px-3 py-2.5
            transition-colors focus-within:border-ink-faint"
        >
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            maxLength={MAX_LENGTH}
            onChange={event => setValue(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder || 'Write a message...'}
            aria-label="Message"
            className="max-h-[200px] flex-1 resize-none border-0 bg-transparent py-1.5 text-[15px] leading-6
              text-ink outline-none ring-0 placeholder:text-ink-faint focus:ring-0"
          />

          <button
            type="submit"
            disabled={disabled || !value.trim()}
            aria-label="Send message"
            className="mb-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg
              bg-accent text-on-accent transition-opacity hover:opacity-90
              disabled:cursor-not-allowed disabled:bg-elevated disabled:text-ink-faint"
          >
            <HiOutlineArrowUp size={16} />
          </button>
        </div>
      </div>
    </form>
  );
}
